import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";

import {
  GetDipDataSourceStatsDocument,
  type GetDipDataSourceStatsQuery,
  type GetDipDataSourceStatsQueryVariables,
} from "~/features/dataImportProjects/gqls";
import type { DipDataSourceStatsSummary } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";

const EMPTY_STATS: DipDataSourceStatsSummary = {
  dataRowsCount: 0,
  readyRowsCount: 0,
};

const LOADING_STATS: DipDataSourceStatsSummary = {
  dataRowsCount: null,
  readyRowsCount: null,
};

export function useDipDataSourceStatsSummary(dataSourceIds: string[]) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const client = useApolloClient();
  const [summary, setSummary] = useState<DipDataSourceStatsSummary>(LOADING_STATS);

  const refetch = useCallback(
    async (isStale: () => boolean = () => false) => {
      if (dataSourceIds.length === 0) {
        if (!isStale()) setSummary(EMPTY_STATS);
        return;
      }

      try {
        const results = await Promise.all(
          dataSourceIds.map((id) =>
            client.query<GetDipDataSourceStatsQuery, GetDipDataSourceStatsQueryVariables>({
              query: GetDipDataSourceStatsDocument,
              variables: { input: { tenantId, id } },
              fetchPolicy: "network-only",
            }),
          ),
        );

        if (isStale()) return;

        let dataRowsCount = 0;
        let readyRowsCount = 0;
        for (const result of results) {
          const stats = result.data?.cnutGetDataSourceStats;
          if (stats?.__typename !== "GetDataSourceStatsOutput") {
            return;
          }
          dataRowsCount += stats.dataRowsCount;
          readyRowsCount += stats.readyRowsCount;
        }
        setSummary({ dataRowsCount, readyRowsCount });
      } catch {
        return;
      }
    },
    [client, dataSourceIds, tenantId],
  );

  useEffect(() => {
    let stale = false;
    void refetch(() => stale);
    return () => {
      stale = true;
    };
  }, [refetch]);

  return { summary, refetch };
}
